import { addLocale, useLocale } from 'ttag'

import dayjs from './dayjsConfig'

import { Lang } from './services/api.types'
import { Preferences } from '@capacitor/preferences'

export const languages = [
  // English (default)
  { lang: Lang.EN, name: 'English' },

  // Albanian
  { lang: Lang.SQ, name: 'Shqip' },
  // Arabic (RTL)
  { lang: Lang.AR, name: 'العربية' }, // shorter, standard form
  // Bulgarian
  { lang: Lang.BG, name: 'Български' },
  // Chinese (simplified)
  { lang: Lang.ZH_HANS, name: '简体中文' },
  // Croatian
  { lang: Lang.HR, name: 'Hrvatski' },
  // Czech
  { lang: Lang.CS, name: 'Čeština' },
  // Danish
  { lang: Lang.DA, name: 'Dansk' },
  // Dutch
  { lang: Lang.NL, name: 'Nederlands' },
  // Estonian
  { lang: Lang.ET, name: 'Eesti' },
  // Finnish
  { lang: Lang.FI, name: 'Suomi' },
  // French
  { lang: Lang.FR, name: 'Français' },
  // German
  { lang: Lang.DE, name: 'Deutsch' },
  // Greek
  { lang: Lang.EL, name: 'Ελληνικά' },
  // Hungarian
  { lang: Lang.HU, name: 'Magyar' },
  // Italian
  { lang: Lang.IT, name: 'Italiano' },
  // Japanese
  { lang: Lang.JA, name: '日本語' },
  // Korean
  { lang: Lang.KO, name: '한국어' },
  // Latvian
  { lang: Lang.LV, name: 'Latviešu' },
  // Lithuanian
  { lang: Lang.LT, name: 'Lietuvių' },
  // Norwegian
  { lang: Lang.NO, name: 'Norsk' },
  // Polish
  { lang: Lang.PL, name: 'Polski' },
  // Portuguese (Portugal)
  { lang: Lang.PT, name: 'Português (Portugal)' },
  // Portuguese (Brazil)
  { lang: Lang.PT_BR, name: 'Português (Brasil)' },
  // Romanian
  { lang: Lang.RO, name: 'Română' },
  // Russian
  { lang: Lang.RU, name: 'Русский' },
  // Serbian
  { lang: Lang.SR, name: 'Српски' },
  // Slovak
  { lang: Lang.SK, name: 'Slovenčina' },
  // Slovenian
  { lang: Lang.SL, name: 'Slovenščina' },
  // Spanish
  { lang: Lang.ES, name: 'Español' },
  // Swedish
  { lang: Lang.SV, name: 'Svenska' },
  // Turkish
  { lang: Lang.TR, name: 'Türkçe' },
  // Ukrainian
  { lang: Lang.UK, name: 'Українська' },

  // Indonesian
  { lang: Lang.ID, name: 'Bahasa Indonesia' },
  // Thai
  { lang: Lang.TH, name: 'ไทย' },
  // Vietnamese
  { lang: Lang.VI, name: 'Tiếng Việt' },
  // Hebrew (RTL)
  { lang: Lang.HE, name: 'עברית' },

  /* Added in 2025 (12 new): */

  // Chinese (traditional)
  { lang: Lang.ZH_HANT, name: '繁體中文' },
  // Hindi
  { lang: Lang.HI, name: 'हिन्दी' },
  // Bengali
  { lang: Lang.BN, name: 'বাংলা' },
  // Punjabi
  { lang: Lang.PA, name: 'ਪੰਜਾਬੀ' },
  // Urdu (RTL)
  { lang: Lang.UR, name: 'اُردُو' },
  // Persian (RTL)
  { lang: Lang.FA, name: 'فارسی' },
  // Malay (Bahasa Melayu)
  { lang: Lang.MS, name: 'Bahasa Melayu' },
  // Filipino
  { lang: Lang.FIL, name: 'Filipino' },
  // Swahili
  { lang: Lang.SW, name: 'Kiswahili' },
  // Hausa
  { lang: Lang.HA, name: 'Hausa' },
  // Burmese (Myanmar)
  { lang: Lang.MY, name: 'မြန်မာစာ' },
  // Tamil
  { lang: Lang.TA, name: 'தமிழ்' },
].sort((a, b) => (a.name > b.name ? 1 : -1))

/*languages.forEach(({ lang, name }, index) => {
  //import(`moment/locale/${lang.toLowerCase()}`)
  console.log(String(lang).toLowerCase())
})*/

export const LOCALE_COOKIE = '__locale'

export const saveLocale = async (localeString: string) => {
  await Preferences.set({ key: LOCALE_COOKIE, value: localeString })
}

const urlParams = new URLSearchParams(window.location.search)
if (urlParams.has('lang')) {
  const locale = String(urlParams.get('lang'))
  saveLocale(locale)
}

export const getLang = async () => {
  let lang = Lang.EN

  // Leaderboard should always be in English.
  if (window.location && window.location.pathname) {
    if (window.location.pathname.split('/')[1] === 'leaderboard') return lang
  }

  const { value: rawLocale } = await Preferences.get({ key: LOCALE_COOKIE })
  if (rawLocale) {
    const localLang = rawLocale.toUpperCase()
    if (localLang in Lang) {
      lang = localLang as Lang
    }
  }
  //else if (isNative()) {
  else {
    // Auto set language based on browser language hint.
    const { language } = navigator as any
    if (language) {
      const parts = language.split('-')
      // Handle Chinese language variants - Traditional Chinese variants map to zh_hant, Simplified to zh_hans
      let localLang: string
      if (parts[0] === 'zh') {
        const langLower = language.toLowerCase()
        // Traditional Chinese variants: zh-TW (Taiwan), zh-HK (Hong Kong), zh-MO (Macau), zh-Hant
        if (langLower === 'zh-tw' || langLower === 'zh-hk' || langLower === 'zh-mo' || langLower === 'zh-hant') {
          localLang = 'zh_hant'
        } else {
          // Simplified Chinese variants: zh-CN, zh-SG, zh-Hans, etc.
          localLang = 'zh_hans'
        }
      } else {
        localLang = parts[0] === 'nb' ? 'no' : parts[0]
      }
      const found = languages.find(({ lang }, index, obj) => String(lang).toLowerCase() === localLang)
      if (found) {
        lang = found.lang
      }
    }
  }
  return lang
}

const getLocale = (lang: Lang) => {
  switch (lang) {
    case Lang.EN:
      return navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.language || 'en-GB'
    case Lang.NO:
      return 'nb'
    case Lang.ZH_HANS:
      return 'zh-cn'
    case Lang.ZH_HANT:
      return 'zh-tw'
    case Lang.PA:
      return 'pa-in'
    case Lang.PT_BR:
      return 'pt-br'
    default:
      return String(lang).toLowerCase()
  }
}

export const currentLang = await getLang()
export const currentLangString = String(currentLang).toLowerCase()
export const isRtl = [Lang.AR, Lang.HE, Lang.UR, Lang.FA].includes(currentLang)

export const currentLocale = getLocale(currentLang)

// Lazy-load only the dayjs locale we need
if (currentLocale !== 'en') {
  try {
    if (currentLocale === 'ha') {
      await import('./locales/dayjs-ha')
    } else if (currentLocale === 'fil') {
      await import('./locales/dayjs-fil')
    } else {
      await import(`dayjs/locale/${currentLocale.toLowerCase()}.js`)
    }
  } catch {
    // Locale file not found — dayjs falls back to English
  }
}
dayjs.locale(currentLocale)

if (currentLang !== Lang.EN && languages.find(({ lang }, index, obj) => lang === currentLang)) {
  try {
    const translationsObj = (await import(`../i18n/${currentLangString}.po.json`)).default
    addLocale(currentLangString, translationsObj)
    useLocale(currentLangString)

    const lang = currentLang === Lang.ZH_HANS ? 'zh-Hans' : currentLang === Lang.ZH_HANT ? 'zh-Hant' : currentLangString
    document.getElementsByTagName('html')[0].setAttribute('lang', lang)
  } catch (e) {
    console.error(`Error loading ${currentLangString}.po`)
  }
}

// For RTL languages, set direction right-to-left.
if (isRtl) {
  document.getElementsByTagName('html')[0].setAttribute('dir', 'rtl')
}
